import React, { useMemo, useRef } from 'react';
import { useRequest } from 'ahooks';
import { ProTablePlus } from '@bedrock/eed-react';
import { IProTablePlusColumn } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import { Tag, Tooltip } from '@bedrock/components';
import NoData from '@bedrock/components/es/Empty/images/small/NoData';
import { SupplierRequestInvoker, supplierDefs, supplierEnums } from '@purchase/supplier';
import './XpcSection.less';

const DEFAULT_PAGE_SIZE = 10;

interface XpcOpusTableProps {
  vendorId: string;
}

function formatCount(v: number | undefined): string {
  if (v == null) return '';
  if (v >= 10000) return `${(v / 10000).toFixed(1)}w`;
  return String(v);
}

function renderTagList(values: string[] | undefined, maxShow = 2) {
  if (!values || values.length === 0) return '-';
  const visible = values.slice(0, maxShow);
  const rest = values.slice(maxShow);
  return (
    <div className="flex flex-wrap gap-[4px]">
      {visible.map((v) => (
        <Tag key={v} color={'light-grey'} size='xSmall' text={v} />
      ))}
      {rest.length > 0 && (
        <Tooltip title={rest.join('、')}>
          <Tag className="cursor-pointer" size='xSmall'  color={'light-grey'} text={`+${rest.length}`}/>
        </Tooltip>
      )}
    </div>
  );
}

function renderHonorTags(honors: supplierDefs.XpcOpusHonorDto[] | undefined) {
  if (!honors || honors.length === 0) return '-';
  const titles = honors.map((h) => h.title).filter(Boolean) as string[];
  if (titles.length === 0) return '-';
  const visible = titles.slice(0, 2);
  const rest = titles.slice(2);
  return (
    <div className="flex flex-wrap gap-[4px]">
      {visible.map((t, i) => (
        <Tag key={i} color="light-orange" size='xSmall'  text={t} />
      ))}
      {rest.length > 0 && (
        <Tooltip
          title={
            <div className="flex flex-col gap-[4px]">
              {rest.map((t, i) => (
                <span key={i}>{t}</span>
              ))}
            </div>
          }
        >
          <Tag className="cursor-pointer" size='xSmall' color={'light-grey'} text={`+${rest.length}`}/>
        </Tooltip>
      )}
    </div>
  );
}

export function XpcOpusTable({ vendorId }: XpcOpusTableProps) {
  const tableRef = useRef<IProTableRef<any>>();

  // 作品分类选项
  const { data: typesData } = useRequest(
    () => SupplierRequestInvoker.getInstance().vendorQueryOpusTypes({ vendorId }),
    { refreshDeps: [vendorId] },
  );

  // 片中职务选项
  const { data: positionsData } = useRequest(
    () => SupplierRequestInvoker.getInstance().vendorQueryOpusPositions({ vendorId }),
    { refreshDeps: [vendorId] },
  );

  const typeOptions = useMemo(
    () => ((typesData?.data as string[] | undefined) ?? []).map((v) => ({ name: v, value: v })),
    [typesData],
  );

  const positionOptions = useMemo(
    () => ((positionsData?.data as string[] | undefined) ?? []).map((v) => ({ name: v, value: v })),
    [positionsData],
  );

  // ProTablePlus request.queryService — 由表格内部驱动筛选+分页
  const queryService = async (query: any) => {
    const { current, pageSize, paramsWithConditions, ...other } = query?.params ?? {};
    console.log('query', query);
    try {
      const res = await SupplierRequestInvoker.getInstance().vendorQueryOpusList({
        vendorId,
        page: current ?? 1,
        pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
        ...other
      });
      const pageData = res?.data as supplierDefs.VendorXpcOpusPageDto | undefined;
      return {
        data: {
          list: pageData?.records ?? [],
          total: pageData?.total ?? 0,
        },
      };
    } catch {
      return { data: { list: [], total: 0 } };
    }
  };

  const columns: IProTablePlusColumn[] = useMemo(
    () => [
      {
        title: '#',
        dataIndex: '_index',
        width: 50,
        fixed: 'left' as const,
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      {
        title: '榜单分类',
        dataIndex: 'resultCategory',
        width: 120,
        render: (v: string) => (
          <span className="xpc-ranking-category">
            {v ? (supplierEnums.VendorXpcRankingCategoryEnum.pick(v)?.name || v) : '-'}
          </span>
        ),
      },
      {
        title: '作品分类',
        dataIndex: 'opusTypes',
        width: 260,
        filterRender: {
          name: 'select',
          props: { options: typeOptions, multiple: true },
        },
        render: (values: string[]) => renderTagList(values),
      },
      {
        title: '作品名称',
        dataIndex: 'opusName',
        width: 250,
        render: (_: unknown, record: supplierDefs.VendorXpcOpusDto) => (
          <div>
            <a
              href={record.opusLink}
              target="_blank"
              rel="noreferrer"
              className="xpc-opus-name__link"
              title={record.opusName}
            >
              {record.opusName || '-'}
            </a>
            {(record.opusPlay != null || record.opusPopularity != null) && (
              <div className="xpc-opus-name__meta">
                {record.opusPlay != null && (
                  <span>{formatCount(record.opusPlay)} 播放</span>
                )}
                {record.opusPlay != null && record.opusPopularity != null && (
                  <span className="mx-[6px]">·</span>
                )}
                {record.opusPopularity != null && (
                  <span>{formatCount(record.opusPopularity)} 人气</span>
                )}
              </div>
            )}
          </div>
        ),
      },
      {
        title: '片中职务',
        dataIndex: 'opusPositions',
        width: 130,
        filterRender: {
          name: 'select',
          props: { options: positionOptions, multiple: true },
        },
        render: (values: string[]) =>
          values && values.length > 0 ? values.join('、') : '-',
      },
      {
        title: '活动荣誉',
        dataIndex: 'opusHonors',
        width: 320,
        render: (honors: supplierDefs.XpcOpusHonorDto[]) => renderHonorTags(honors),
      },
    ],
    [typeOptions, positionOptions],
  );

  return (
    <ProTablePlus
      ref={tableRef as any}
      columns={columns}
      scroll={{ x: true }}
      request={{ queryService }}
      pagination={{ pageSize: DEFAULT_PAGE_SIZE }}
      emptyText={
        <div className="rock-empty">
          <NoData className="rock-empty-image" />
          <div className="rock-empty-text">暂无作品数据</div>
        </div>
      }
    />
  );
}